/**
 * Custom hooks for Convex messaging
 * Wraps Convex queries and mutations with user context
 */

import { useQuery, useMutation } from "@pronteer/convex/react"
import { api as convex } from "@pronteer/convex/api"
import { Id } from "@pronteer/convex/dataModel"

/**
 * Get all conversations for the current user
 * Note: conversations.list requires companyId and crewId for multi-tenancy
 */
export function useConversations(companyId: string | undefined, crewId: string | undefined) {
  return useQuery(
    convex.conversations.list,
    companyId && crewId ? { companyId, crewId } : "skip"
  )
}

/**
 * Get a single conversation by ID
 */
export function useConversation(conversationId: Id<"conversations"> | null, crewId: string | undefined) {
  return useQuery(
    convex.conversations.get,
    conversationId && crewId ? { conversationId, crewId } : "skip"
  )
}

// Note: For messages, use useMessages from convex-hooks.ts which loads the full conversation

/**
 * Get typing indicators for a conversation
 */
export function useTypingIndicators(conversationId: Id<"conversations"> | null, crewId: string | undefined) {
  return useQuery(
    convex.typing.list,
    conversationId && crewId ? { conversationId, crewId } : "skip"
  )
}

/**
 * Get presence for a user
 */
export function usePresence(crewId: string | null) {
  return useQuery(
    convex.presence.get,
    crewId ? { crewId } : "skip"
  )
}

/**
 * Get presence for multiple users
 */
export function useBulkPresence(crewIds: string[]) {
  return useQuery(
    convex.presence.getBulk,
    crewIds.length > 0 ? { crewIds } : "skip"
  )
}

/**
 * Get unread notifications count
 */
export function useUnreadNotifications(crewId: string | undefined) {
  return useQuery(
    convex.notifications.getUnreadCount,
    crewId ? { crewId } : "skip"
  )
}

/**
 * Mutation: Create a new conversation
 */
export function useCreateConversation() {
  return useMutation(convex.conversations.create)
}

/**
 * Mutation: Find or create a DM conversation
 */
export function useFindOrCreateConversation() {
  return useMutation(convex.conversations.findOrCreate)
}

/**
 * Mutation: Send a message
 */
export function useSendMessage() {
  return useMutation(convex.messages.send)
}

/**
 * Mutation: Mark conversation as read
 */
export function useMarkConversationAsRead() {
  return useMutation(convex.conversations.markAsRead)
}

/**
 * Mutation: Set typing indicator
 */
export function useSetTyping() {
  return useMutation(convex.typing.setTyping)
}

/**
 * Mutation: Clear typing indicator
 */
export function useClearTyping() {
  return useMutation(convex.typing.clearTyping)
}

/**
 * Mutation: Update presence status
 */
export function useUpdatePresence() {
  return useMutation(convex.presence.update)
}

/**
 * Mutation: Set user as online
 */
export function useSetOnline() {
  return useMutation(convex.presence.setOnline)
}

/**
 * Mutation: Set user as offline
 */
export function useSetOffline() {
  return useMutation(convex.presence.setOffline)
}

/**
 * Mutation: Delete a conversation
 */
export function useDeleteConversation() {
  return useMutation(convex.conversations.deleteConversation)
}

/**
 * Mutation: Toggle mute
 */
export function useToggleMute() {
  return useMutation(convex.conversations.toggleMute)
}

/**
 * Mutation: Toggle pin
 */
export function useTogglePin() {
  return useMutation(convex.conversations.togglePin)
}
